import type { CSRangeConfig } from '../types/CSRangeConfig';
import type { CSRules } from '../types/CSRules';
import type { CSQueryPayload } from '../types/CSQueryPayload';
import { CSCountQuery } from '../queries/CSCountQuery';
import { CSTrendQuery } from '../queries/CSTrendQuery';
import { CSTrendWidgetQuery } from '../queries/CSTrendWidgetQuery';
import { CSQueryableEventType } from '../types/CSQueryableEventType';

import { CSQuery } from '../queries/CSQuery';
import PAYLOADS from '../payloads';

interface QueryMetadata {
    name: string;
    applyMediaFilter: boolean;
    applyDomainFilter?: boolean;
    applyAdvertiserIdentifierFilter?: boolean;
}

const QueryRegistry = {
    TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_CLICK_TO_CALLS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_IDENTIFIED_VISITORS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_SERVICE_PAGE_VIEWS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_SPECIALS_PAGE_VISITS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_SRP_VIEWS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS_VDP_VIEWS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_CONVERSIONS: {
        name: 'TOTAL_AD_INFLUENCED_CONVERSIONS',
        applyMediaFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_ICAN: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_ICAN',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC_TOTAL_VISITS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },
    TOTAL_AD_INFLUENCED_TRAFFIC: {
        name: 'TOTAL_AD_INFLUENCED_TRAFFIC',
        applyMediaFilter: true,
    },
    TOTAL_CONVERSIONS_AMAZON_DIRECT: {
        name: 'TOTAL_CONVERSIONS_AMAZON_DIRECT',
        applyMediaFilter: false,
    },
    TOTAL_CONVERSIONS_CLICK_TO_CALLS: {
        name: 'TOTAL_CONVERSIONS_CLICK_TO_CALLS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS_DIRECT: {
        name: 'TOTAL_CONVERSIONS_DIRECT',
        applyMediaFilter: false,
    },
    TOTAL_CONVERSIONS_IDENTIFIED_VISITORS: {
        name: 'TOTAL_CONVERSIONS_IDENTIFIED_VISITORS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS_ORGANIC: {
        name: 'TOTAL_CONVERSIONS_ORGANIC',
        applyMediaFilter: false,
    },
    TOTAL_CONVERSIONS_PAID_SEARCH: {
        name: 'TOTAL_CONVERSIONS_PAID_SEARCH',
        applyMediaFilter: false,
    },
    TOTAL_CONVERSIONS_REFERRAL: {
        name: 'TOTAL_CONVERSIONS_REFERRAL',
        applyMediaFilter: false,
    },
    TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS: {
        name: 'TOTAL_CONVERSIONS_SERVICE_PAGE_VIEWS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS_SOCIAL: {
        name: 'TOTAL_CONVERSIONS_SOCIAL',
        applyMediaFilter: false,
    },
    TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS: {
        name: 'TOTAL_CONVERSIONS_SPECIALS_PAGE_VISITS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS_SRP_VIEWS: {
        name: 'TOTAL_CONVERSIONS_SRP_VIEWS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS: {
        name: 'TOTAL_CONVERSIONS_TOTAL_HOURS_AND_DIRECTIONS_VIEWS',
        type: 'event',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS_VDP_VIEWS: {
        name: 'TOTAL_CONVERSIONS_VDP_VIEWS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_CONVERSIONS: { name: 'TOTAL_CONVERSIONS', applyMediaFilter: false },
    TOTAL_UNIQUE_TRAFFIC_DIRECT: {
        name: 'TOTAL_UNIQUE_TRAFFIC_DIRECT',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC_ICAN: {
        name: 'TOTAL_UNIQUE_TRAFFIC_ICAN',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH: {
        name: 'TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH: {
        name: 'TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC_REFERRAL: {
        name: 'TOTAL_UNIQUE_TRAFFIC_REFERRAL',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC_SOCIAL: {
        name: 'TOTAL_UNIQUE_TRAFFIC_SOCIAL',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS: {
        name: 'TOTAL_UNIQUE_TRAFFIC_TOTAL_VISITS',
        applyMediaFilter: false,
        applyDomainFilter: true,
    },
    TOTAL_UNIQUE_TRAFFIC: {
        name: 'TOTAL_UNIQUE_TRAFFIC',
        applyMediaFilter: false,
    },

    TREND_IMPRESSIONS: {
        name: 'TREND_IMPRESSIONS',
        applyMediaFilter: true,
        applyAdvertiserIdentifierFilter: true,
    },

    TREND_VISITS: {
        name: 'TREND_VISITS',
        applyMediaFilter: true,
        applyDomainFilter: true,
    },

    TREND_IMPRESSIONS_VERSUS_VISITS: {
        name: 'TREND_IMPRESSIONS_VERSUS_VISITS',
        applyMediaFilter: true,
    },
};

type QueryRegistryKey = keyof typeof QueryRegistry;

interface QueryRegistryFactoryOptions {
    apiToken: string;
    isDuplicateApiToken: boolean;
    browserTimezone?: string;
    rangeConfig?: CSRangeConfig;
    rules?: CSRules;

    range?: {
        startDate: number;
        endDate: number;
        period: string;
    };
    mediaType?: string;
    domain?: string;
    advertiserId?: number;
}

enum MediaType {
    All = '',
    Audio = 'Audio',
    Display = 'Display',
    StreamingTV = 'STV',
    OnlineVideo = 'OLV',
    Video = 'Video',
}

const toMediaType = (value: string): MediaType => {
    switch (value) {
        case 'Streaming TV':
            return MediaType.StreamingTV;
        case 'Online Video':
            return MediaType.OnlineVideo;
        case 'Audio':
            return MediaType.Audio;
        case 'Display':
            return MediaType.Display;
        case 'Video':
            return MediaType.Video;
        default:
            return MediaType.All;
    }
};

const useQueryRegistry = () => {
    /**
     * Factory method to instantiate a query based on the registry.
     *
     * @param queryRegistryKey - A key from the QueryRegistry.
     * @param options - Common options such as apiToken and any payload overrides.
     * @returns A configured instance of CSQuery.
     */
    const buildQuery = (
        queryRegistryKey: QueryRegistryKey,
        options: QueryRegistryFactoryOptions
    ): CSQuery | CSCountQuery | null => {
        const metadata = QueryRegistry[queryRegistryKey];

        if (!metadata) {
            throw new Error(`No query metadata defined for ${queryRegistryKey}`);
        }

        let query: CSQuery | null = null;
        let queryPayload: CSQueryPayload | null = null;

        if (PAYLOADS[metadata.name as QueryRegistryKey] !== undefined) {
            queryPayload = JSON.parse(JSON.stringify(PAYLOADS[metadata.name as QueryRegistryKey]));
        }

        if (queryPayload !== null) {
            switch (queryPayload.queryType) {
                case 'CSCountQuery':
                    query = new CSCountQuery(queryPayload.countField, queryPayload.namedEvent);
                    break;

                case 'CSTrendQuery':
                    query = new CSTrendQuery(
                        queryPayload.countField,
                        queryPayload.namedEvent,
                        queryPayload?.cummulativeAgg || null,
                        queryPayload?.cummulativeAggWindowSize || null
                    );
                    break;

                case 'CSTrendWidgetQuery':
                    query = new CSTrendWidgetQuery(
                        queryPayload.countField,
                        queryPayload.namedEvent,
                        queryPayload?.multiSeries,
                        queryPayload?.groupBys
                    );
                    break;
            }

            if (query !== null) {
                query.setRules(queryPayload?.rules || []);

                if (options.apiToken) {
                    query.setApiToken(options.apiToken);
                }
                if (options.browserTimezone) {
                    query.setBrowserTimezone(options.browserTimezone);
                }
                if (options.rangeConfig) {
                    query.setRangeConfig(options.rangeConfig);
                }
                if (options.rules) {
                    const existingRules = query.rules || { operator: 'AND', rules: [] };

                    if (!Array.isArray(existingRules.rules)) {
                        existingRules.rules = [];
                    }

                    if (existingRules.operator === 'AND') {
                        existingRules.rules.push(options.rules);
                    } else if (existingRules.operator === 'OR') {
                        existingRules.rules = [
                            {
                                operator: 'AND',
                                rules: [existingRules, options.rules],
                            },
                        ];
                    }

                    query.setRules(existingRules);
                }
                if (options.range) {
                    query.setRange(options.range);
                }

                if (metadata.applyMediaFilter) {
                    if (options?.mediaType) {
                        if (queryRegistryKey === 'TREND_IMPRESSIONS') {
                            query.rules?.rules?.push({
                                operator: 'AND',
                                removable: true,
                                rules: [
                                    {
                                        operator: 'EQUAL',
                                        operand: {
                                            name: '__medium',
                                            type: 'STRING',
                                            category: 'EVENT',
                                        },
                                        argument: toMediaType(options.mediaType).toLowerCase(),
                                    },
                                ],
                            });
                        } else {
                            query.rules?.rules?.push({
                                operator: 'AND',
                                removable: true,
                                rules: [
                                    {
                                        operator: 'EQUAL_IC',
                                        operand: 'S_SOURCE_CATEGORY_2',
                                        argument: toMediaType(options.mediaType),
                                    },
                                ],
                            });
                        }
                    }
                }

                if (options?.isDuplicateApiToken) {
                    if (options?.domain?.length) {
                        if ('applyDomainFilter' in metadata && metadata.applyDomainFilter) {
                            switch (queryPayload?.namedEvent?.queryableEvent?.eventType) {
                                case CSQueryableEventType.CLICK:
                                case CSQueryableEventType.PAGE:
                                case CSQueryableEventType.TRACK:
                                    query.rules?.rules?.push({
                                        operator: 'AND',
                                        tag: null,
                                        locked: false,
                                        removable: true,
                                        rules: [
                                            {
                                                operator: 'CONTAINS_IC',
                                                operand: 'E_PAGE_HOST',
                                                argument: options.domain,
                                            },
                                        ],
                                    });
                                    break;

                                case CSQueryableEventType.IDENTIFY:
                                case CSQueryableEventType.SESSION:
                                case CSQueryableEventType.VISIT:
                                    query.rules?.rules?.push({
                                        operator: 'AND',
                                        tag: null,
                                        locked: false,
                                        removable: true,
                                        rules: [
                                            {
                                                operator: 'CONTAINS_IC',
                                                operand: 'S_LANDING_PAGE_HOST',
                                                argument: options.domain,
                                            },
                                        ],
                                    });
                                    break;
                            }
                        }
                    }

                    if (options?.advertiserId) {
                        if ('applyAdvertiserIdentifierFilter' in metadata && metadata.applyAdvertiserIdentifierFilter) {
                            query.rules?.rules?.push({
                                operator: 'OR',
                                tag: null,
                                locked: false,
                                removable: true,
                                rules: [
                                    {
                                        operator: 'EQUAL',
                                        operand: {
                                            name: '__advertiser_id',
                                            type: 'STRING',
                                            category: 'EVENT',
                                        },
                                        argument: options.advertiserId,
                                    },
                                    {
                                        operator: 'EQUAL',
                                        operand: {
                                            name: '__cas_advertiserid',
                                            type: 'STRING',
                                            category: 'EVENT',
                                        },
                                        argument: options.advertiserId,
                                    },
                                ],
                            });
                        }
                    }
                }
            }
        }

        return query;
    };

    return { buildQuery };
};

export type { QueryMetadata, QueryRegistryKey };
export { QueryRegistry, useQueryRegistry };
